import React, { useEffect, useId, useRef, useState } from "react";
import { Body, Label, SectionHeader } from "@sprout/typography";
import { TextInput } from "@sprout/text_input";
import { IconButton } from "@sprout/icon_button";
import FileIcon from "@sprout/file_icon";
import CloseIcon from "@sprout/icons/close";
import { Websites } from "@components/quick_apply/websites";
import EducationSection from "@components/quick_apply/education_section";
import { Employment, Employments } from "@components/quick_apply/employments";
import type { Education } from "@components/quick_apply/education_section.tsx";
import { useEffectAfterMount } from "@hooks/use_effect_after_mount";
import {
  EducationErrors,
  EmploymentErrors,
  validateEmployments,
  validateEducations,
  validateResume,
  MAX_FILE_SIZE_MB,
} from "@components/quick_apply/util";
import ProfileButtons from "@components/profile_buttons";
import UnloadHandler from "@components/unload_handler";
import { SelfIdentificationForm } from "@components/self_identification_form";
import "./quick_apply.scss";
import useTranslation from "@hooks/use_translation";
import { Location } from "@components/ui/location_input";
import { SelfIdentification } from "@/utils/self_identification";
import { useShowFlash } from "@sprout/flash.tsx";
import { useQueryClient } from "@tanstack/react-query";
import Container from "@components/ui/container.tsx";
import BackButton from "@components/back_button.tsx";
import { LocationInput } from "@components/ui/location_input.tsx";
import { ProfileData, FileState } from "@/queries/use_profile";
import axios from "axios";
import DeleteResumeModal from "@components/quick_apply/delete_resume_modal";
import SaveChangesModal from "@components/quick_apply/save_changes_modal";
import { PhoneInput, type PhoneError } from "@sprout/phone_input.tsx";
import { hasFirstRecord, writeToFieldIfEmpty } from "@/lib/utils.ts";
import UploadResumeModal from "@components/quick_apply/upload_resume_modal.tsx";
import { SecondaryButton } from "@sprout/buttons";
import { router, usePage } from "@inertiajs/react";
import { useSnowplowTracker, SnowplowEvent } from "@/hooks/use_tracker";
import { useSafePostHog } from "@/hooks/use_posthog";
import { flushSync } from "react-dom";

const phoneNumberErrorMapping: Record<
  Exclude<PhoneError, "missing_country" | "unknown">,
  string
> = {
  invalid: "is invalid",
  too_short: "is too short",
  too_long: "is too long",
};

type ServerError = {
  error: unknown;
};

interface ParseResponse extends ProfileData {
  resumeUploadId: number;
}

type ResumeUploadParseData = ParseResponse & {
  resumeFile?: { filename: string; url: string; content_type: string };
};

type QuickApplyProps = ProfileData & {
  resumeUploadParseData?: ResumeUploadParseData | null;
};

const QuickApply = (props: QuickApplyProps) => {
  const { paths } = props;
  const { uploadResumeModal } = usePage<{ uploadResumeModal?: boolean }>().props;
  const variant = uploadResumeModal ? "upload_resume_modal" : "default";
  const { t } = useTranslation("user");
  const showFlash = useShowFlash();
  const queryClient = useQueryClient();
  const { trackEvent } = useSnowplowTracker();
  const posthog = useSafePostHog();

  const [firstName, setFirstName] = useState(props.firstName || "");
  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [lastName, setLastName] = useState(props.lastName || "");
  const [lastNameError, setLastNameError] = useState<string | null>(null);
  const [preferredName, setPreferredName] = useState(props.preferredName || "");
  const [preferredNameError, setPreferredNameError] = useState<string | null>(null);
  const [initialPhoneNumber, setInitialPhoneNumber] = useState<string>(props.phoneNumber || "");
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [phoneNumberRealtimeError, setPhoneNumberRealtimeError] = useState<PhoneError | null>(null);
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);
  const [countryError, setCountryError] = useState<string | null>(null);
  const [location, setLocation] = useState<Location | null>(props.location);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resume, setResume] = useState<FileState | null>(props.resume);
  const [resumeErrors, setResumeErrors] = useState<string | undefined>("");
  const [websites, setWebsites] = useState<Websites>(props.websites || {});
  const [educations, setEducations] = useState(
    props.educations.map((e) => ({ ...e, key: crypto.randomUUID().toString() })) || [],
  );
  const [educationsError, setEducationsError] = useState<EducationErrors>({});
  const [employments, setEmployments] = useState<Employment[]>(
    (props.employments.length > 0 ? props.employments : [{}]).map((employment) => ({
      ...employment,
      key: crypto.randomUUID().toString(),
    })),
  );
  const [employmentsErrors, setEmploymentsErrors] = useState<EmploymentErrors>({});
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [isDeleteResumeModalOpen, setIsDeleteResumeModalOpen] = useState(false);
  const [isUploadResumeModalOpen, setIsUploadResumeModalOpen] = useState(false);
  const [parseResponse, setParseResponse] = useState<ParseResponse | null | ServerError>(null);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [autofillOnReceive, setAutofillOnReceive] = useState(false);
  const [resumeUploadId, setResumeUploadId] = useState<null | number>(null);
  const [fromResumeUpload, setFromResumeUpload] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);

  const bypassUnsavedChangesModalRef = useRef(false);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const preferredNameRef = useRef<HTMLInputElement>(null);
  const countryRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const resumeParseInputRef = useRef<HTMLInputElement>(null);
  const resumeDivRef = useRef<HTMLDivElement>(null);
  const educationRefs = useRef<Array<HTMLElement>>([]);
  const employmentRefs = useRef<Array<HTMLElement>>([]);
  let errorScrollEl: HTMLInputElement | HTMLElement | null = null;

  const firstNameId = useId();
  const lastNameId = useId();

  const [selfIdentification, setSelfIdentification] = useState<SelfIdentification>(
    props.selfIdentification.value || {},
  );

  useEffectAfterMount(() => {
    setUnsavedChanges(true);
  }, [
    firstName,
    firstNameError,
    lastName,
    lastNameError,
    phoneNumberError,
    preferredName,
    preferredNameError,
    location,
    educations,
    employments,
    selfIdentification,
  ]);

  useEffect(() => {
    if (unsavedChanges) {
      return router.on("before", (e) => {
        if (e.detail.visit.method === "get" && !bypassUnsavedChangesModalRef.current) {
          e.preventDefault();
          setPendingUrl(e.detail.visit.url.pathname);
          setIsSaveChangesModalOpen(true);
        }
      });
    }
  }, [unsavedChanges]);

  useEffectAfterMount(() => {
    // Ignore the case where a user had previously saved a phone number like "+1 (555) 666-7777"
    // by manually typing it in before we had the new phone number input. The phone number input
    // will clean this up and store it as "+15556667777" upon mount. We don't want to warn the
    // user about unsaved changes in this case since they didn't actually change anything.
    if (phoneNumber !== initialPhoneNumber.replace(/[^0-9+]/g, "")) {
      setUnsavedChanges(true);
    }
  }, [phoneNumber]);

  function isError(response: ProfileData | ServerError): response is ServerError {
    return (response as ServerError).error !== undefined;
  }

  useEffect(() => {
    const modalFlowDone =
      !isUploadResumeModalOpen || (isUploadResumeModalOpen && autofillOnReceive);

    if (modalFlowDone && !uploadingResume) {
      if (isUploadResumeModalOpen) {
        setIsUploadResumeModalOpen(false);
      } else {
        if (parseResponse && !isError(parseResponse) && parseResponse.resumeUploadId) {
          showFlash(t("resume.uploaded"), "success");
          setResumeUploadId(parseResponse.resumeUploadId);

          autofillOnReceive && populateForm(parseResponse as ParseResponse);
        } else if (parseResponse) {
          showFlash(t("resume.error_flash"), "error");
        }
      }
    }
  }, [uploadingResume, autofillOnReceive, isUploadResumeModalOpen, parseResponse]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("from_resume_upload") === "true" && props.resumeUploadParseData) {
      const parseData = props.resumeUploadParseData;
      setFromResumeUpload(true);
      setUploadingResume(true);
      window.history.replaceState({}, "", window.location.pathname);

      const timer = setTimeout(() => {
        setUploadingResume(false);

        if (parseData.resumeFile) {
          setResume({
            file: null,
            filename: parseData.resumeFile.filename,
            url: parseData.resumeFile.url,
            content_type: parseData.resumeFile.content_type,
          });
        }

        if (parseData.resumeUploadId) {
          setResumeUploadId(parseData.resumeUploadId);
          populateForm(parseData, { showFlashNotifications: false });
          showFlash(t("resume.profile_completed_from_resume"), "success");
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleParseResume = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const resumeUpload = {
        file: file,
        filename: file.name,
        url: URL.createObjectURL(file),
        content_type: file.type,
      } as FileState;

      const resumeError = validateResume(resumeUpload);

      if (resumeError) {
        setResume(resumeUpload);
        setResumeErrors(t(resumeError, { size: MAX_FILE_SIZE_MB }));
        return;
      }

      setResumeErrors("");
      setUploadingResume(true);
      trackEvent(SnowplowEvent.ResumeUploadStarted, {
        source: "quick_apply",
        file_type: file.type,
        file_size: file.size,
        variant,
      });
      setAutofillOnReceive(false);
      setParseResponse(null);
      setIsUploadResumeModalOpen(true);

      const formData = new FormData();
      formData.append("resume", file);

      try {
        const response = await axios.post(paths.resumeUploadPath, formData);

        if (response.data) {
          setResume(resumeUpload);
          setParseResponse(response.data);
          posthog?.capture("resume_uploaded", {
            source: "quick_apply",
            upload_method: "file_picker",
          });
        }
        setUploadingResume(false);
        trackEvent(SnowplowEvent.ResumeUploadCompleted, {
          source: "quick_apply",
          resume_upload_id: response.data?.resumeUploadId,
          variant,
        });
      } catch (e) {
        setUploadingResume(false);
        setParseResponse({ error: e });
        trackEvent(SnowplowEvent.ResumeUploadFailed, {
          source: "quick_apply",
          error_reason: e instanceof Error ? e.message : "unknown",
          variant,
        });
      }
    }
  };

  const populateForm = (newProfile: ParseResponse, { showFlashNotifications = true } = {}) => {
    try {
      let dirty = false;

      dirty = writeToFieldIfEmpty(firstName, newProfile.firstName, setFirstName) || dirty;
      dirty = writeToFieldIfEmpty(lastName, newProfile.lastName, setLastName) || dirty;
      dirty =
        writeToFieldIfEmpty(preferredName, newProfile.preferredName, setPreferredName) || dirty;

      if (!phoneNumber && newProfile.phoneNumber) {
        setInitialPhoneNumber(newProfile.phoneNumber);
        setPhoneNumber(newProfile.phoneNumber);
        dirty = true;
      }

      setWebsites((prevState) => {
        if (!newProfile.websites) {
          return prevState;
        }
        return Object.keys(websites).reduce((newWebsites, siteName) => {
          // @ts-expect-error TS7053
          newWebsites[siteName] = prevState[siteName];
          // @ts-expect-error TS7053
          if (!prevState[siteName] && newProfile.websites[siteName]) {
            // @ts-expect-error TS7053
            newWebsites[siteName] = newProfile.websites[siteName];
            dirty = true;
          }
          return newWebsites;
        }, {} as Websites);
      });

      if (!hasFirstRecord(educations) && hasFirstRecord(newProfile.educations)) {
        dirty = true;
        setEducations(
          newProfile.educations.map((education: Education) => ({
            ...education,
            key: crypto.randomUUID().toString(),
          })),
        );
      }

      // @ts-expect-error TS2345
      if (!hasFirstRecord(employments) && hasFirstRecord(newProfile.employments)) {
        dirty = true;
        setEmployments(
          (newProfile.employments.length > 0 ? newProfile.employments : [{}]).map((employment) => ({
            ...employment,
            key: crypto.randomUUID().toString(),
          })),
        );
      }

      if (dirty) {
        setUnsavedChanges(true);
        validateForm();
        posthog?.capture("resume_autofill_completed");
        if (showFlashNotifications) showFlash(t("resume.autofilled"), "success");
      } else {
        if (showFlashNotifications) showFlash(t("resume.autofill_failed"), "error");
      }
    } catch (err) {
      if (showFlashNotifications) showFlash(t("resume.autofill_failed"), "error");
    }
  };

  const setIfFirstErrorEl = (ref: HTMLInputElement | HTMLElement | null) => {
    if (!errorScrollEl) {
      errorScrollEl = ref;
    }
  };

  const scrollToInputError = () => {
    if (!errorScrollEl) {
      return;
    }
    errorScrollEl.scrollIntoView({ behavior: "smooth", block: "center", inline: "start" });
  };

  const attachParseResumeClick = () => {
    if (resumeParseInputRef.current) {
      resumeParseInputRef.current.click();
    }
  };

  const removeResumeFromPage = () => {
    setResume(null);
    setResumeUploadId(null);
  };

  const deleteFile = async () => {
    try {
      await axios.delete("/resume");
      showFlash(t("resume.deleted"));
      setResume(null);
      removeResumeFromPage();
    } catch (e) {
      showFlash(t("resume.error"), "error");
    }
  };

  const removeResume = () => {
    if (resume?.id == null) {
      removeResumeFromPage();
    } else {
      setIsDeleteResumeModalOpen(true);
    }
  };

  const validateForm = () => {
    errorScrollEl = null;

    let newPhoneNumberError = null;
    let newCountryError = null;
    if (phoneNumber.length > 0 && phoneNumberRealtimeError !== null) {
      if (phoneNumberRealtimeError === "missing_country") {
        newCountryError = "Select a country";
        setIfFirstErrorEl(countryRef.current);
      } else if (phoneNumberRealtimeError !== "unknown") {
        newPhoneNumberError = phoneNumberErrorMapping[phoneNumberRealtimeError];
        setIfFirstErrorEl(phoneRef.current);
      }
    }

    const resumeError = validateResume(resume);
    resumeError && setIfFirstErrorEl(resumeDivRef.current);

    const educationErrors = validateEducations(educations);
    const hasEducationErrors = Object.keys(educationErrors).length > 0;
    if (hasEducationErrors) {
      const firstError = Object.keys(educationErrors)[0];
      setIfFirstErrorEl(educationRefs.current[firstError as unknown as number]);
    }

    const employmentErrors = validateEmployments(employments);
    const hasEmploymentErrors = Object.keys(employmentErrors).length > 0;
    if (hasEmploymentErrors) {
      const firstError = Object.keys(employmentErrors)[0];
      setIfFirstErrorEl(employmentRefs.current[firstError as unknown as number]);
    }

    if (
      newCountryError ||
      newPhoneNumberError ||
      resumeError ||
      hasEducationErrors ||
      hasEmploymentErrors
    ) {
      setCountryError(newCountryError);
      setPhoneNumberError(newPhoneNumberError);
      setResumeErrors(resumeError ? t(resumeError, { size: MAX_FILE_SIZE_MB }) : "");
      setEducationsError(educationErrors);
      setEmploymentsErrors(employmentErrors);
      scrollToInputError();
      return true;
    }

    return false;
  };

  const submit = async () => {
    const foundError = validateForm();
    if (foundError) {
      return true;
    }

    setIsSubmitting(true);
    setError(null);

    setFirstNameError(null);
    setLastNameError(null);
    setCountryError(null);
    setPhoneNumberError(null);
    setPreferredNameError(null);
    setResumeErrors("");
    setEducationsError({});
    setEmploymentsErrors({});

    const formData = new FormData();

    formData.append("user[first_name]", firstName);
    formData.append("user[last_name]", lastName);
    formData.append("personal_info[preferred_name]", preferredName);
    formData.append("personal_info[phone_number]", phoneNumber);
    if (location) {
      formData.append("location[display_name]", location.display_name);
      formData.append("location[latitude]", location.latitude.toString());
      formData.append("location[longitude]", location.longitude.toString());

      // This might be an empty string if the provider doesn't know the country code,
      // or null if the user had saved their location before we started collecting it
      if (location.country_short_name) {
        formData.append("location[country_short_name]", location.country_short_name);
      }
    }

    if (resumeUploadId) {
      formData.append("resume_upload_id", resumeUploadId.toString());
    } else if (resume?.file) {
      formData.append("resume", resume.file);
    }

    formData.append("websites[linkedin]", websites.linkedin || "");
    formData.append("websites[github]", websites.github || "");
    formData.append("websites[portfolio]", websites.portfolio || "");
    formData.append("websites[website]", websites.website || "");

    formData.append("educations", JSON.stringify(educations));

    formData.append("employments", JSON.stringify(employments));

    formData.append(
      "self_identification[disability_status]",
      selfIdentification.disabilityStatus?.toString() || "",
    );
    formData.append("self_identification[gender]", selfIdentification.gender?.toString() || "");
    formData.append("self_identification[race]", selfIdentification.race?.toString() || "");
    formData.append(
      "self_identification[veteran_status]",
      selfIdentification.veteranStatus?.toString() || "",
    );

    if (pendingUrl) {
      formData.append("redirect_url", pendingUrl);
      setPendingUrl(null);
    }

    router.patch(paths.quickApplyPath, formData, {
      onSuccess: () => {
        trackEvent(SnowplowEvent.ResumeUploaded, {
          source: fromResumeUpload ? "modal" : "quick_apply",
          resume_upload_id: resumeUploadId ?? undefined,
          autofilled: fromResumeUpload ? 1 : 0,
          variant,
        });
        posthog?.capture("quick_apply_submitted");
        showFlash(t("profile_updated.success"), "success");
        setUnsavedChanges(false);
        queryClient.invalidateQueries({ queryKey: ["whoami"] });
      },
      onError: (errors) => {
        if (errors.first_name) {
          setIfFirstErrorEl(firstNameRef.current);
          setFirstNameError(errors.first_name[0]);
        }

        if (errors.last_name) {
          setIfFirstErrorEl(lastNameRef.current);
          setLastNameError(errors.last_name[0]);
        }

        if (errors.preferred_name) {
          setIfFirstErrorEl(preferredNameRef.current);
          setPreferredNameError(errors.preferred_name[0]);
        }

        if (errors.phone_number) {
          setIfFirstErrorEl(phoneRef.current);
          setPhoneNumberError(errors.phone_number[0]);
        }

        if (errors.resume) {
          setIfFirstErrorEl(resumeDivRef.current);
          setResumeErrors(errors.resume[0]);
        }

        if (!errorScrollEl) {
          showFlash(t("profile_updated.error"), "error");
        }

        scrollToInputError();
      },
      onFinish: () => {
        setIsSubmitting(false);
      },
    });
  };

  return (
    <Container>
      {unsavedChanges && <UnloadHandler />}
      <div
        onClickCapture={(e) => {
          if (uploadResumeModal && fromResumeUpload && unsavedChanges) {
            e.preventDefault();
            e.stopPropagation();
            setIsSaveChangesModalOpen(true);
          }
        }}
      >
        <BackButton href="/profile" />
      </div>
      <div className="flex flex-col gap-[4px]">
        <div className="quick_apply__header">
          <SectionHeader as="h1">{t("edit_quick_apply")}</SectionHeader>
        </div>
        <Body>{t("quickApplyDescription")}</Body>
      </div>
      <div className="quick_apply__documents">
        <div className="quick_apply__resume" ref={resumeDivRef}>
          <Label className={resumeErrors ? "label--error" : ""} id="resume" htmlFor="resume">
            {t("resume.header")}
          </Label>
          {resume ? (
            <div className="quick_apply__selected_documents">
              <FileIcon contentType={resume.content_type} />
              <a href={resume.id && resume.url} target="_blank" rel="noopener noreferrer">
                <Body>{resume.filename}</Body>
              </a>
              <IconButton label={t("resume.remove")} icon={CloseIcon} onClick={removeResume} />
            </div>
          ) : (
            <>
              <SecondaryButton
                shape="pill"
                loading={isUploadResumeModalOpen || uploadingResume}
                onClick={attachParseResumeClick}
              >
                {t("resume.add")}
              </SecondaryButton>
              <input
                type="file"
                id="resume-upload"
                accept=".pdf,.doc,.docx,.txt,.rtf"
                ref={resumeParseInputRef}
                onChange={handleParseResume}
              />
              <Body metadata>{t("resume.acceptedFileTypes")}</Body>
            </>
          )}
          {resumeErrors && (
            <p id="resume-error" className="helper-text helper-text--error" aria-live="polite">
              {resumeErrors}
            </p>
          )}
        </div>
      </div>
      <div className="quick_apply__details">
        <div className="quick_apply__short-field">
          <TextInput
            id={firstNameId}
            ref={firstNameRef}
            label={t("fields.firstName")}
            onChange={(event) => setFirstName(event.currentTarget.value || "")}
            maxLength={255}
            value={firstName}
            error={firstNameError && `First name ${firstNameError}`}
          />
        </div>
        <div className="quick_apply__short-field">
          <TextInput
            id={lastNameId}
            ref={lastNameRef}
            label={t("fields.lastName")}
            onChange={(event) => setLastName(event.currentTarget.value || "")}
            maxLength={255}
            value={lastName}
            error={lastNameError && `Last name ${lastNameError}`}
          />
        </div>
        <div className="quick_apply__short-field">
          <TextInput
            id="preferred-name"
            ref={preferredNameRef}
            label={t("fields.preferredName")}
            onChange={(event) => setPreferredName(event.currentTarget.value || "")}
            maxLength={255}
            value={preferredName}
            error={preferredNameError && `Preferred first name ${preferredNameError}`}
          />
        </div>

        <div className="quick_apply__short-field">
          <PhoneInput
            id="phone"
            key={initialPhoneNumber}
            countryRef={countryRef}
            phoneRef={phoneRef}
            countryLabel={t("fields.country")}
            phoneLabel={t("fields.phone")}
            initialValue={initialPhoneNumber}
            onChange={setPhoneNumber}
            onError={setPhoneNumberRealtimeError}
            maxLength={255}
            countryError={countryError}
            phoneError={phoneNumberError && `Phone number ${phoneNumberError}`}
          />
        </div>

        <div className="location">
          <LocationInput value={location} onChange={setLocation} />
        </div>
      </div>
      <EducationSection
        educations={educations}
        setEducations={setEducations}
        educationsError={educationsError}
        educationPath={paths.educationPath}
        educationRefs={educationRefs}
      />
      <Employments
        employments={employments}
        errors={employmentsErrors}
        setEmployments={setEmployments}
        employmentRefs={employmentRefs}
      />
      <Websites websites={websites} setWebsites={setWebsites} />
      <SelfIdentificationForm
        {...props.selfIdentification}
        value={selfIdentification}
        onChange={setSelfIdentification}
      />

      <ProfileButtons
        loading={isSubmitting}
        error={error}
        submit={submit}
        paths={paths}
        showDelete={false}
        onCancel={() => {
          bypassUnsavedChangesModalRef.current = true;
        }}
      />

      <DeleteResumeModal
        open={isDeleteResumeModalOpen}
        onOpenChange={setIsDeleteResumeModalOpen}
        onDeleteResume={deleteFile}
      />

      <UploadResumeModal
        open={isUploadResumeModalOpen}
        onOpenChange={setIsUploadResumeModalOpen}
        onSelectAutofill={() => {
          if (!uploadingResume) {
            setIsUploadResumeModalOpen(false);
          }
          setAutofillOnReceive(true);
        }}
        showWaiting={autofillOnReceive}
      />

      <SaveChangesModal
        open={isSaveChangesModalOpen}
        onOpenChange={setIsSaveChangesModalOpen}
        onLeaveWithoutSaving={() => {
          flushSync(() => {
            setIsSaveChangesModalOpen(false);
            setUnsavedChanges(false);
          });
          bypassUnsavedChangesModalRef.current = true;
          router.visit(pendingUrl || paths.profilePath);
          setPendingUrl(null);
        }}
        onSave={() => {
          setIsSaveChangesModalOpen(false);
          submit();
        }}
      />
    </Container>
  );
};

export default QuickApply;
